import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ZoomIn, ZoomOut, Grid, Magnet, Undo, Redo, AlignLeft, AlignCenterHorizontal, AlignRight, Maximize2, RotateCcw } from "lucide-react";

interface CanvasToolbarProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomFit?: () => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  snapToGrid: boolean;
  onToggleSnap: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignRight: () => void;
  onResetAllBlocks?: () => void;
}

export function CanvasToolbar({
  zoom,
  onZoomChange,
  onZoomFit,
  showGrid,
  onToggleGrid,
  snapToGrid,
  onToggleSnap,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onResetAllBlocks,
}: CanvasToolbarProps) {
  const displayZoom = Math.round(zoom * 10) * 10;
  
  const handleZoomIn = () => {
    const newZoom = Math.min(2, zoom + 0.1);
    onZoomChange(Math.round(newZoom * 10) / 10);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(0.25, zoom - 0.1);
    onZoomChange(Math.round(newZoom * 10) / 10);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-3 p-2 border-b bg-card">
        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={handleZoomOut}>
                <ZoomOut className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Zoom Out (⌘ -)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={() => onZoomChange(1)}>
                <span className="text-sm font-medium w-12 text-center">{displayZoom}%</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset to 100% (⌘ 0)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={handleZoomIn}>
                <ZoomIn className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Zoom In (⌘ +)</p>
            </TooltipContent>
          </Tooltip>

          {onZoomFit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onZoomFit}>
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Fit to Screen</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Grid Controls */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant={showGrid ? "default" : "ghost"} onClick={onToggleGrid}>
                <Grid className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Show Grid (⌘ G)</p>
              <p className="text-xs text-muted-foreground">Displays 16px alignment grid</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant={snapToGrid ? "default" : "ghost"} onClick={onToggleSnap}>
                <Magnet className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Snap to Grid</p>
              <p className="text-xs text-muted-foreground">Auto-aligns blocks to grid lines</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Alignment Tools */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={onAlignLeft}>
                <AlignLeft className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Align Left</p>
              <p className="text-xs text-muted-foreground">Aligns selected blocks to the left</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={onAlignCenter}>
                <AlignCenterHorizontal className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Align Center</p>
              <p className="text-xs text-muted-foreground">Centers selected blocks horizontally</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={onAlignRight}>
                <AlignRight className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Align Right</p>
              <p className="text-xs text-muted-foreground">Aligns selected blocks to the right</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" disabled={!canUndo} onClick={onUndo}>
                <Undo className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Undo (⌘ Z)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" disabled={!canRedo} onClick={onRedo}>
                <Redo className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Redo (⌘ Shift Z)</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Reset All Blocks */}
        {onResetAllBlocks && (
          <>
            <Separator orientation="vertical" className="h-6" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onResetAllBlocks}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset All Positions</p>
                <p className="text-xs text-muted-foreground">Cascade all blocks from top-left</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
